

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from "api";
import { STATUS } from "utils/status";

interface AiValidationHistoryPayload {
  sub_project_id?: string;
  project_id?: string;
  page?: number;
  page_size?: number;
  association_type?: string;
  status?: string;
  run_type?: string;
  type?: string;
  stage?: string;
  config?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
}

interface AiValidationSummary {
  total_runs: number;
  pass_rate: number;
  avg_latency_ms: number;
  total_tokens: number;
  estimated_total_cost: number;
}

interface AiValidationHistoryResponse {
  status: boolean;
  summary: AiValidationSummary;
  count: number;
  page_size: number;
  current_page: number;
  total_pages: number;
  data: Record<string, unknown>[];
}

interface AiValidationHistoryState {
  aiValidationHistory: {
    count: number;
    results: Record<string, unknown>[];
    summary: AiValidationSummary;
  };
  getStatus: STATUS;
  error: string | null;
}

const initialState: AiValidationHistoryState = {
  aiValidationHistory: {
    count: 0,
    results: [],
    summary: {
      total_runs: 0,
      pass_rate: 0,
      avg_latency_ms: 0,
      total_tokens: 0,
      estimated_total_cost: 0,
    },
  },
  getStatus: STATUS.NOT_STARTED,
  error: null,
};

export const getAiValidationHistory = createAsyncThunk(
  'project/ai-validation/history/get',
  async (payload: AiValidationHistoryPayload = {}, thunkAPI) => {
    try {
      const response = await api.get<AiValidationHistoryResponse>('/project/ai-validation/history/', {
        params: {
          sub_project_id: payload.sub_project_id,
          project_id: payload.sub_project_id ? undefined : payload.project_id,
          page: payload.page,
          page_size: payload.page_size,
          association_type: payload.association_type,
          status: payload.status,
          run_type: payload.run_type,
          type: payload.type,
          stage: payload.stage,
          config: payload.config,
          search: payload.search,
          start_date: payload.start_date,
          end_date: payload.end_date,
        },
      });
      const responseData = response.data;
      const mappedResults = Array.isArray(responseData.data) ? responseData.data : [];

      return {
        count: Number(responseData.count ?? mappedResults.length ?? 0),
        results: mappedResults,
        summary: {
          total_runs: Number(responseData.summary?.total_runs ?? 0),
          pass_rate: Number(responseData.summary?.pass_rate ?? 0),
          avg_latency_ms: Number(responseData.summary?.avg_latency_ms ?? 0),
          total_tokens: Number(responseData.summary?.total_tokens ?? 0),
          estimated_total_cost: Number(responseData.summary?.estimated_total_cost ?? 0),
        },
      };
    } catch (error: any) {
      if (error.response?.data?.message) {
        return thunkAPI.rejectWithValue(error.response.data.message);
      }
      return thunkAPI.rejectWithValue('Unable to fetch AI validation history');
    }
  }
);

const aiValidationHistorySlice = createSlice({
  name: 'aiValidationHistory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAiValidationHistory.pending, (state) => {
        state.getStatus = STATUS.PENDING;
        state.error = null;
      })
      .addCase(getAiValidationHistory.fulfilled, (state, action) => {
        state.getStatus = STATUS.SUCCESS;
        state.aiValidationHistory.count = action.payload.count;
        state.aiValidationHistory.results = action.payload.results;
        state.aiValidationHistory.summary = action.payload.summary;
      })
      .addCase(getAiValidationHistory.rejected, (state, action) => {
        state.getStatus = STATUS.FAILED;
        state.error = typeof action.payload === 'string' ? action.payload : 'Unable to fetch AI validation history';
      });
  },
});

export default aiValidationHistorySlice.reducer;



